import React, { useState, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { updateOrder, deleteOrder, deleteAllCompletedOrders } from '../store/slices/ordersSlice';
import { OrderCard } from '../features/orders/components/OrderCard';
import { OrderSummary } from '../features/orders/components/OrderSummary';
import { Order } from '../types/order';
import { CalendarEvent } from '../types/calendar';
import { Recipe } from '../types/recipe';

interface MonthGroup {
  monthYear: string; // Format: "YYYY-MM" for sorting
  displayName: string; // Format: "December 2025"
  orders: Order[];
}

export const CompletedOrders: React.FC = () => {
  const dispatch = useAppDispatch();
  const completedOrders = useAppSelector(state => state.orders.completedOrders);
  const calendarEvents = useAppSelector(state => state.calendar.events);
  const fillings = useAppSelector(state => state.recipes.fillings);
  const [highlightedOrderId, setHighlightedOrderId] = useState<string | null>(null);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());
  const orderRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Sort completed orders by completion date (most recently completed first)
  const sortedCompletedOrders = [...completedOrders].sort((a, b) => {
    // Find the delivery event completion timestamp for each order
    const eventA = calendarEvents.find((event: CalendarEvent) => event.orderId === a.id && event.completed);
    const eventB = calendarEvents.find((event: CalendarEvent) => event.orderId === b.id && event.completed);

    // Use event updatedAt (completion time) if available, otherwise fall back to orderDate
    const timeA = eventA?.updatedAt ? new Date(eventA.updatedAt).getTime() : new Date(a.orderDate).getTime();
    const timeB = eventB?.updatedAt ? new Date(eventB.updatedAt).getTime() : new Date(b.orderDate).getTime();

    return timeB - timeA; // Most recent first
  });

  // Group orders by month
  const groupOrdersByMonth = (orders: Order[]): MonthGroup[] => {
    const monthMap = new Map<string, Order[]>();

    orders.forEach(order => {
      const date = new Date(order.orderDate);
      const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      if (!monthMap.has(monthYear)) {
        monthMap.set(monthYear, []);
      }
      monthMap.get(monthYear)!.push(order);
    });

    // Convert to array and sort by month (newest first)
    const monthGroups: MonthGroup[] = Array.from(monthMap.entries())
      .map(([monthYear, orders]) => {
        const [year, month] = monthYear.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1);
        const displayName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

        return {
          monthYear,
          displayName,
          orders
        };
      })
      .sort((a, b) => b.monthYear.localeCompare(a.monthYear));

    return monthGroups;
  };

  const monthGroups = groupOrdersByMonth(sortedCompletedOrders);

  // Only use fillings for order items (not components)
  const recipeOptions = fillings.map((r: Recipe) => ({
    id: r.id,
    name: r.name
  }));

  const handleUpdateOrder = (updatedOrder: any) => {
    dispatch(updateOrder(updatedOrder));
  };

  const handleDeleteOrder = (id: string) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      dispatch(deleteOrder(id));
    }
  };

  const handleDeleteAllCompletedOrders = () => {
    if (
      window.confirm(
        `Are you sure you want to delete all ${completedOrders.length} completed order${completedOrders.length !== 1 ? 's' : ''}? This action cannot be undone.`
      )
    ) {
      dispatch(deleteAllCompletedOrders());
    }
  };

  // Handle navigation from flavor summary to specific order
  const handleNavigateToOrder = (orderId: string) => {
    // Expand the order (accordion behavior - only one at a time)
    setExpandedOrderId(orderId);

    // Highlight the order
    setHighlightedOrderId(orderId);

    // Scroll to the order after a brief delay to ensure the DOM is updated
    setTimeout(() => {
      const orderElement = orderRefs.current[orderId];
      if (orderElement) {
        orderElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }, 100);

    // Remove highlight after 2 seconds
    setTimeout(() => {
      setHighlightedOrderId(null);
    }, 2500);
  };

  // Handle toggling order expansion with accordion behavior
  const handleToggleOrder = (orderId: string) => {
    setExpandedOrderId(prevId => (prevId === orderId ? null : orderId));
  };

  // Handle toggling month accordion
  const handleToggleMonth = (monthYear: string) => {
    setExpandedMonths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(monthYear)) {
        newSet.delete(monthYear);
      } else {
        newSet.add(monthYear);
      }
      return newSet;
    });
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <header style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '2rem', color: '#333', margin: '0 0 8px 0' }}>Completed Orders</h1>
          <p style={{ color: '#666', margin: 0 }}>
            {completedOrders.length} completed order{completedOrders.length !== 1 ? 's' : ''}
          </p>
        </div>

        <button
          onClick={handleDeleteAllCompletedOrders}
          disabled={completedOrders.length === 0}
          style={{
            padding: '12px 24px',
            backgroundColor: completedOrders.length === 0 ? '#ccc' : '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: completedOrders.length === 0 ? 'not-allowed' : 'pointer',
            fontSize: '1rem',
            fontWeight: 'bold',
            opacity: completedOrders.length === 0 ? 0.6 : 1
          }}
        >
          Delete All Completed Orders
        </button>
      </header>

      {/* Status Summary */}
      <div
        style={{
          marginBottom: '24px'
        }}
      >
        <div
          style={{
            padding: '16px',
            backgroundColor: '#E8F5E9',
            borderLeft: '4px solid #4CAF50',
            borderRadius: '4px'
          }}
        >
          <h3 style={{ margin: '0 0 8px 0', color: '#4CAF50' }}>Completed Orders</h3>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0 }}>{completedOrders.length}</p>
        </div>
      </div>

      {/* Order Summary - Units by Flavor (for completed orders only) */}
      <OrderSummary orders={completedOrders} onNavigateToOrder={handleNavigateToOrder} />

      {/* Completed Orders List - Grouped by Month */}
      <div>
        {sortedCompletedOrders.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              color: '#999'
            }}
          >
            <p style={{ fontSize: '1.2rem', margin: '0 0 12px 0' }}>No completed orders yet</p>
            <p style={{ margin: 0 }}>Complete delivery tasks in the schedule page to see orders here</p>
          </div>
        ) : (
          monthGroups.map(monthGroup => {
            const isExpanded = expandedMonths.has(monthGroup.monthYear);
            return (
              <div key={monthGroup.monthYear} style={{ marginBottom: '16px' }}>
                {/* Month Header - Accordion Toggle */}
                <div
                  onClick={() => handleToggleMonth(monthGroup.monthYear)}
                  style={{
                    padding: '16px 20px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'background-color 0.2s',
                    userSelect: 'none'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = '#e9ecef';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span
                      style={{
                        fontSize: '1.25rem',
                        fontWeight: 600,
                        color: '#212529'
                      }}
                    >
                      {monthGroup.displayName}
                    </span>
                    <span
                      style={{
                        padding: '4px 12px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        fontWeight: 600
                      }}
                    >
                      {monthGroup.orders.length} {monthGroup.orders.length === 1 ? 'order' : 'orders'}
                    </span>
                  </div>
                  <span
                    style={{
                      fontSize: '1.5rem',
                      color: '#6c757d',
                      transition: 'transform 0.2s',
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}
                  >
                    ▼
                  </span>
                </div>

                {/* Month Orders - Collapsible Content */}
                {isExpanded && (
                  <div style={{ marginTop: '8px', paddingLeft: '0px' }}>
                    {monthGroup.orders.map(order => (
                      <OrderCard
                        key={order.id}
                        ref={el => {
                          orderRefs.current[order.id] = el;
                        }}
                        order={order}
                        recipes={recipeOptions}
                        onUpdate={handleUpdateOrder}
                        onDelete={handleDeleteOrder}
                        isHighlighted={highlightedOrderId === order.id}
                        forceExpanded={expandedOrderId === order.id}
                        onToggle={() => handleToggleOrder(order.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
